/*
 * Copyright © 2015-2021 Serenova, LLC. All rights reserved.
 */

/**
 *
 * QueuesForm
 *
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FieldArray } from 'redux-form/immutable';

import FiltersComponent from './components/FiltersComponent';
import DetailWrapper from '../../../components/DetailWrapper';
import { PriorityField } from './components/Fields/PriorityField';
import { DetailHeader, InputField, SelectField, LoadingSpinnerSVG } from 'cx-ui-components';
import store from '../../../redux/store';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const WrappedDetailHeader = styled(DetailHeader)`
  margin-left: 35px;
`;
const StyledDetailHeader = styled(DetailHeader)`
  flex-grow: 1;
  margin: 0px !important;
`;
const QueryHeaderWrapper = styled.div`
  display: flex;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 10px;
  margin-left: 35px;
`;
const SwitchQueryTypeLink = styled.span`
  display: inline-block;
  font-size: 14px;
  color: #3498db;
  cursor: pointer;
  :hover {
    color: #07487a;
  }
`;
const QueueSlaErrorMsg = styled.span`
  font-size: 13px;
  color: red;
  margin-left: 160px;
`;

class QueuesForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showQueryType: null,
    };
  }
  switchQueryType = (queryType) => {
    this.setState({ showQueryType: queryType });
  };
  render() {
    return (
      <form onSubmit={this.props.handleSubmit} key={this.props.key}>
        <Wrapper>
          {!this.props.isAllDependentEntitiesFetched || this.props.isSaving ? (
            <LoadingSpinnerSVG size={60} />
          ) : (
            <Fragment>
              <DetailWrapper open data-automation="queueDetailsSVG">
                <WrappedDetailHeader text="Details" />
                <InputField
                  name="name"
                  label="Name *"
                  inputType="text"
                  componentType="input"
                  data-automation="nameInput"
                  disabled={this.props.isSaving || !this.props.userHasUpdatePermission}
                />
                <InputField
                  name="description"
                  label="Description"
                  inputType="text"
                  componentType="textarea"
                  data-automation="descriptionInput"
                  disabled={this.props.isSaving || !this.props.userHasUpdatePermission}
                />
                {this.props.selectedEntityId === 'create' ? (
                  <InputField
                    disabled
                    name="activeVersion"
                    label="Active Version"
                    inputType="text"
                    componentType="input"
                    alignInptTextToRight
                    data-automation="activeVersionInput"
                  />
                ) : (
                  <SelectField
                    name="activeVersion"
                    label="Active Version *"
                    options={this.props.selectedQueueVersionsNames}
                    data-automation="activeVersionDropDownList"
                    disabled={this.props.isSaving || !this.props.userHasUpdatePermission}
                  />
                )}
              </DetailWrapper>

              {this.props.selectedEntityId === 'create' && (
                <Fragment>
                  <DetailWrapper open data-automation="queueQuerySVG">
                    <QueryHeaderWrapper>
                      <StyledDetailHeader text="Query" />
                      {(!this.state.showQueryType || this.state.showQueryType === 'basic') && (
                        <SwitchQueryTypeLink
                          onClick={() => {
                            this.switchQueryType('advanced');
                            this.props.convertQueueQueryObjToEdn(this.props.basicQuery);
                          }}>
                          (Show advanced query)
                        </SwitchQueryTypeLink>
                      )}
                      {this.state.showQueryType === 'advanced' && (
                        <SwitchQueryTypeLink
                          onClick={() => {
                            this.switchQueryType('basic');
                            store.dispatch({ type: 'CONVERT_EDN_STRING_TO_IMMUTABLE' });
                          }}>
                          (Show basic query)
                        </SwitchQueryTypeLink>
                      )}
                    </QueryHeaderWrapper>

                    {this.state.showQueryType === 'advanced' ? (
                      <InputField
                        name="query"
                        label="Query"
                        inputType="text"
                        componentType="textarea"
                        data-automation="advacnedQueryInput"
                        disabled={this.props.isSaving || !this.props.userHasUpdatePermission}
                      />
                    ) : (
                      <FieldArray
                        name="basicQuery"
                        rerenderOnEveryChange
                        component={FiltersComponent}
                        props={{
                          change: this.props.change,
                          disabled: this.props.isSaving || !this.props.userHasUpdatePermission,
                          basicQuery: this.props.basicQuery,
                          dependentEntities: this.props.dependentEntities,
                          userHasUpdatePermission: this.props.userHasUpdatePermission,
                          querylevelCountInSelectedVersion: this.props.querylevelCountInSelectedVersion,
                          matchmaker2Enabled: this.props.matchmaker2Enabled,
                        }}
                      />
                    )}
                  </DetailWrapper>

                  <DetailWrapper open data-automation="queuePrioritySVG">
                    <WrappedDetailHeader text="Priority" />
                    <PriorityField disabled={this.props.isSaving || !this.props.userHasUpdatePermission} />
                  </DetailWrapper>

                  <DetailWrapper open data-automation="queueSlaSVG">
                    <WrappedDetailHeader text="Service Level Agreement" />
                    <SelectField
                      name="slaId"
                      label="Queue SLA"
                      options={this.props.tenantSlas}
                      data-automation="queueSlaList"
                      disabled={this.props.isSaving || !this.props.userHasUpdatePermission}
                    />
                    <QueueSlaErrorMsg>
                      Interactions already completed or in queue won't be impacted by this change.
                    </QueueSlaErrorMsg>
                  </DetailWrapper>
                </Fragment>
              )}
              {this.props.selectedEntityId !== 'create' && (
                <DetailHeader
                  userHasUpdatePermission={this.props.userHasUpdatePermission}
                  text="Versions"
                  onActionButtonClick={() => this.props.setSelectedSubEntityId('create')}
                  open
                />
              )}
            </Fragment>
          )}
        </Wrapper>
      </form>
    );
  }
}

QueuesForm.propTypes = {
  basicQuery: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  change: PropTypes.func,
  convertQueueQueryObjToEdn: PropTypes.func,
  dependentEntities: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isAllDependentEntitiesFetched: PropTypes.bool,
  isSaving: PropTypes.bool,
  key: PropTypes.string,
  matchmaker2Enabled: PropTypes.bool,
  querylevelCountInSelectedVersion: PropTypes.number,
  selectedEntityId: PropTypes.string,
  selectedQueueVersionsNames: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  setSelectedSubEntityId: PropTypes.func,
  tenantSlas: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  userHasUpdatePermission: PropTypes.bool,
};

export default QueuesForm;
